import type { ColorTheme } from 'fitify-ui-onboarding/src/types/theme'
import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'

import { ALL_IMAGES, DEFAULT_IMAGE_VARIANT } from 'data/images'
import type {
  CategoryPageImages,
  CategoryPages,
  CategoryVariants,
  ImageCategories,
  ImageDefinition,
} from 'types/images'

export type ImageParams = {
  locale?: string
  theme?: ColorTheme
}

type GetImageOptions<
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
> = ImageParams & {
  variant?: TVariant | (string & {})
}

export const resolveImageParams = (
  image: ImageDefinition,
  { locale, theme }: ImageParams = {}
): ImageDefinition => {
  if (!locale && !theme) {
    return image
  }
  return {
    ...image,
    srcSet: image.srcSet.map((src) =>
      src
        .replaceAll('{{locale}}', locale ?? '{{locale}}')
        .replaceAll('{{theme}}', theme ?? '{{theme}}')
    ),
  }
}

const getImage = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage,
  { variant, ...params }: GetImageOptions<TCategory, TVariant> = {}
): ImageDefinition => {
  const resolvedVariant = (variant ?? DEFAULT_IMAGE_VARIANT) as TVariant
  const image = ALL_IMAGES[category][resolvedVariant][page][
    imageName
  ] as ImageDefinition

  return resolveImageParams(image, params)
}

export const processImageDefinition = (
  imageDefinition: ImageDefinition
): ImageProps => {
  return {
    src: imageDefinition?.srcSet[0] || '',
    srcSet:
      imageDefinition?.srcSet.map((x, i) => `${x} ${i + 1}x`).join(',') || '',
    width: imageDefinition?.width || 0,
    height: imageDefinition?.height || 0,
  }
}

export const getImageProps = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage,
  options: GetImageOptions<TCategory, TVariant> = {}
): ImageProps => {
  const imageDefinition = getImage(category, page, imageName, options)
  return processImageDefinition(imageDefinition)
}
